import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const sectionLinks = [
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#work", label: "Work" },
  { href: "#music", label: "Music" },
  { href: "#credits", label: "Credits" },
  { href: "#contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // When not on home, anchor links should bring user to home then scroll
  const linkHref = (h) => (isHome ? h : `/${h}`);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || !isHome ? "bg-warmwhite/85 backdrop-blur-xl border-b border-taupe/30" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="group flex items-baseline gap-2" data-testid="brand-logo">
          <span className="font-serif text-2xl md:text-3xl tracking-tight leading-none">James</span>
          <span className="font-serif italic text-gold text-xl md:text-2xl leading-none">1:1</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {sectionLinks.map((l) => (
            <a key={l.href} href={linkHref(l.href)} className="text-[11px] uppercase tracking-widest-custom font-medium text-charcoal/80 hover:text-gold transition-colors duration-300" data-testid={`nav-link-${l.label.toLowerCase()}`}>
              {l.label}
            </a>
          ))}
          <Link to="/shop" className="text-[11px] uppercase tracking-widest-custom font-medium text-charcoal/80 hover:text-gold transition-colors duration-300" data-testid="nav-link-store">
            Shop
          </Link>
        </nav>

        <Link
          to="/shop"
          className="hidden lg:inline-flex items-center justify-center bg-charcoal text-warmwhite px-6 py-3 text-[11px] uppercase tracking-widest-custom hover:bg-olive transition-colors duration-300"
          data-testid="nav-book-cta"
        >
          Signal House Shop
        </Link>

        <button
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-charcoal"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-warmwhite border-t border-taupe/30" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {sectionLinks.map((l) => (
              <a key={l.href} href={linkHref(l.href)} onClick={() => setOpen(false)} className="text-sm uppercase tracking-widest-custom text-charcoal/80 hover:text-gold" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
                {l.label}
              </a>
            ))}
            <Link to="/store" onClick={() => setOpen(false)} className="text-sm uppercase tracking-widest-custom text-charcoal/80 hover:text-gold" data-testid="mobile-nav-store">
              Store
            </Link>
            <Link to="/store" onClick={() => setOpen(false)} className="mt-2 inline-flex bg-charcoal text-warmwhite px-6 py-3 text-xs uppercase tracking-widest-custom" data-testid="mobile-book-cta">
              Shop services
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
